import { privateApi, publicApi } from '@/config/axios.config';
import {
  EmailCampaignDTO,
  WhatsAppCampaignDTO,
  EmailCampaignCreateDTO,
  EmailCampaignUpdateDTO,
  WhatsAppCampaignCreateDTO,
  WhatsAppCampaignUpdateDTO,
  EmailCampaignFilterDTO,
  WhatsAppCampaignFilterDTO,
  EmailCampaignListResponse,
  WhatsAppCampaignListResponse,
  EmailCampaignStatsDTO,
  WhatsAppCampaignStatsDTO,
  EmailTemplateDTO,
  WhatsAppTemplateDTO,
  CampaignRecipient,
  WhatsAppWebhookEvent,
  CampaignStatus,
  MarketingSocialPost,
  MarketingPublicationFilters,
  MarketingPublicationStats,
  MarketingSchedulerSlot,
} from '../types/marketing-types';

// #1191 — privateApi.baseURL ya incluye /api/v1 (axios.config.ts); repetirlo
// acá componía /api/v1/api/v1/marketing/* -> 404 silencioso (Promise.allSettled
// + zero-state), Wallet/Presupuesto mostraba $0 sin error visible.
const MARKETING_BASE_URL = '/marketing';
const SOCIAL_POSTS_BASE_URL = `${MARKETING_BASE_URL}/social-posts`;

/**
 * GH#1647 — la API envuelve la respuesta en `{ success, data, timestamp, ... }` y
 * `privateApi` NO la desenvuelve (su interceptor devuelve la respuesta tal cual).
 * Devuelve el `data` de adentro cuando viene el sobre, o el cuerpo si ya vino sin él.
 * Sin esto, `getScheduler` le hacía `.map` al sobre (TypeError) y tumbaba el
 * `Promise.all` de la lista de publicaciones; `getStats` devolvía el sobre y los
 * contadores leían `undefined` -> 0.
 */
export function desenvolverSobre<T>(cuerpo: unknown): T {
  if (
    cuerpo !== null &&
    typeof cuerpo === 'object' &&
    !Array.isArray(cuerpo) &&
    'success' in cuerpo &&
    'data' in cuerpo
  ) {
    return (cuerpo as { data: T }).data;
  }
  return cuerpo as T;
}

interface SocialPostListResponse {
  data: MarketingSocialPost[];
  total: number;
  page: number;
  limit: number;
}

export type SocialPostFilterRequest = Partial<
  Omit<MarketingPublicationFilters, 'dateRange' | 'network' | 'status'> & {
    network?: string;
    status?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }
>;

export interface SocialPostCreatePayload {
  title: string;
  copy: string;
  network: MarketingSocialPost['network'];
  status?: MarketingSocialPost['status'];
  propertyId?: string;
  propertyTitle?: string;
  scheduledAt?: string;
  metadata?: Record<string, any>;
  mediaAssets?: MarketingSocialPost['media'];
}

type SocialPostUpdatePayload = Partial<SocialPostCreatePayload> & {
  metrics?: MarketingSocialPost['metrics'];
};

const buildSocialPostParams = (filters?: SocialPostFilterRequest): string => {
  const params = new URLSearchParams();

  if (!filters) {
    return '';
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

// === EMAIL CAMPAIGNS API ===

export const emailCampaignsApi = {
  // Crear nueva campaña de email
  create: async (data: EmailCampaignCreateDTO): Promise<EmailCampaignDTO> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/email-campaigns`,
      data
    );
    return response.data;
  },

  // Obtener todas las campañas con filtros
  getAll: async (
    filters?: EmailCampaignFilterDTO
  ): Promise<EmailCampaignListResponse> => {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.template) params.append('template', filters.template);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.targetAudience)
      params.append('targetAudience', filters.targetAudience);
    if (filters?.createdBy) params.append('createdBy', filters.createdBy);
    if (filters?.createdFrom)
      params.append('createdFrom', filters.createdFrom.toISOString());
    if (filters?.createdTo)
      params.append('createdTo', filters.createdTo.toISOString());
    if (filters?.scheduledFrom)
      params.append('scheduledFrom', filters.scheduledFrom.toISOString());
    if (filters?.scheduledTo)
      params.append('scheduledTo', filters.scheduledTo.toISOString());
    if (filters?.tags?.length) {
      filters.tags.forEach(tag => params.append('tags', tag));
    }
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns?${params.toString()}`
    );
    // GH#1631 (QA de #1653 en prod): la API manda el sobre; sin desenvolver,
    // `campaigns` se leia en la raiz -> undefined -> "0 campañas" con 2 reales.
    return desenvolverSobre<EmailCampaignListResponse>(response.data);
  },

  // Obtener campaña por ID
  getById: async (id: string): Promise<EmailCampaignDTO> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns/${id}`
    );
    return desenvolverSobre<EmailCampaignDTO>(response.data);
  },

  // Actualizar campaña
  update: async (
    id: string,
    data: EmailCampaignUpdateDTO
  ): Promise<EmailCampaignDTO> => {
    const response = await privateApi.patch(
      `${MARKETING_BASE_URL}/email-campaigns/${id}`,
      data
    );
    return response.data;
  },

  // Eliminar campaña
  delete: async (id: string): Promise<void> => {
    await privateApi.delete(`${MARKETING_BASE_URL}/email-campaigns/${id}`);
  },

  // Obtener estadísticas generales
  getStats: async (): Promise<EmailCampaignStatsDTO> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns/stats`
    );
    // GH#1631: idem getAll — `total` vive en `data`, no en la raiz.
    return desenvolverSobre<EmailCampaignStatsDTO>(response.data);
  },

  // Obtener campañas por estado
  getByStatus: async (status: CampaignStatus): Promise<EmailCampaignDTO[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns/by-status/${status}`
    );
    return response.data;
  },

  // Obtener campañas por tipo
  getByType: async (type: string): Promise<EmailCampaignDTO[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns/by-type/${type}`
    );
    return response.data;
  },

  // Obtener campañas programadas
  getScheduled: async (): Promise<EmailCampaignDTO[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns/scheduled`
    );
    return response.data;
  },

  // Obtener campañas en ejecución
  getRunning: async (): Promise<EmailCampaignDTO[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns/running`
    );
    return response.data;
  },

  // Actualizar estado de campaña
  updateStatus: async (
    id: string,
    status: CampaignStatus
  ): Promise<EmailCampaignDTO> => {
    const response = await privateApi.patch(
      `${MARKETING_BASE_URL}/email-campaigns/${id}/status`,
      { status }
    );
    return response.data;
  },

  // Enviar campaña inmediatamente
  send: async (id: string): Promise<void> => {
    await privateApi.post(`${MARKETING_BASE_URL}/email-campaigns/${id}/send`);
  },

  // Programar campaña
  schedule: async (
    id: string,
    scheduledAt: Date
  ): Promise<EmailCampaignDTO> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/email-campaigns/${id}/schedule`,
      {
        scheduledAt: scheduledAt.toISOString(),
      }
    );
    return response.data;
  },

  // Pausar campaña
  pause: async (id: string): Promise<EmailCampaignDTO> => {
    const response = await privateApi.patch(
      `${MARKETING_BASE_URL}/email-campaigns/${id}/status`,
      {
        status: CampaignStatus.PAUSED,
      }
    );
    return response.data;
  },

  // Reanudar campaña
  resume: async (id: string): Promise<EmailCampaignDTO> => {
    const response = await privateApi.patch(
      `${MARKETING_BASE_URL}/email-campaigns/${id}/status`,
      {
        status: CampaignStatus.RUNNING,
      }
    );
    return response.data;
  },

  // Duplicar campaña
  duplicate: async (id: string, newName: string): Promise<EmailCampaignDTO> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/email-campaigns/${id}/duplicate`,
      {
        name: newName,
      }
    );
    return response.data;
  },

  // Vista previa de campaña
  preview: async (
    id: string,
    recipientEmail?: string
  ): Promise<{ htmlPreview: string; textPreview: string }> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns/${id}/preview`,
      {
        params: { recipientEmail },
      }
    );
    return response.data;
  },

  // Obtener destinatarios de campaña
  getRecipients: async (id: string): Promise<CampaignRecipient[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/email-campaigns/${id}/recipients`
    );
    return response.data;
  },

  // Enviar email de prueba
  sendTest: async (id: string, testEmails: string[]): Promise<void> => {
    await privateApi.post(`${MARKETING_BASE_URL}/email-campaigns/${id}/test`, {
      testEmails,
    });
  },
};

// === WHATSAPP CAMPAIGNS API ===

export const whatsappCampaignsApi = {
  // Crear nueva campaña de WhatsApp
  create: async (
    data: WhatsAppCampaignCreateDTO
  ): Promise<WhatsAppCampaignDTO> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/whatsapp-campaigns`,
      data
    );
    return response.data;
  },

  // Obtener todas las campañas con filtros
  getAll: async (
    filters?: WhatsAppCampaignFilterDTO
  ): Promise<WhatsAppCampaignListResponse> => {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.messageType) params.append('messageType', filters.messageType);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.targetAudience)
      params.append('targetAudience', filters.targetAudience);
    if (filters?.createdBy) params.append('createdBy', filters.createdBy);
    if (filters?.createdFrom)
      params.append('createdFrom', filters.createdFrom.toISOString());
    if (filters?.createdTo)
      params.append('createdTo', filters.createdTo.toISOString());
    if (filters?.scheduledFrom)
      params.append('scheduledFrom', filters.scheduledFrom.toISOString());
    if (filters?.scheduledTo)
      params.append('scheduledTo', filters.scheduledTo.toISOString());
    if (filters?.tags?.length) {
      filters.tags.forEach(tag => params.append('tags', tag));
    }
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns?${params.toString()}`
    );
    // GH#1662: idem email — `campaigns` vive en `data`. El servicio devuelve
    // {campaigns,total,page,limit,totalPages} y el interceptor lo deja entero en `data`.
    return desenvolverSobre<WhatsAppCampaignListResponse>(response.data);
  },

  // Obtener campaña por ID
  getById: async (id: string): Promise<WhatsAppCampaignDTO> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/${id}`
    );
    return response.data;
  },

  // Actualizar campaña
  update: async (
    id: string,
    data: WhatsAppCampaignUpdateDTO
  ): Promise<WhatsAppCampaignDTO> => {
    const response = await privateApi.patch(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/${id}`,
      data
    );
    return response.data;
  },

  // Eliminar campaña
  delete: async (id: string): Promise<void> => {
    await privateApi.delete(`${MARKETING_BASE_URL}/whatsapp-campaigns/${id}`);
  },

  // Obtener estadísticas generales
  getStats: async (): Promise<WhatsAppCampaignStatsDTO> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/stats`
    );
    // GH#1662: sin desenvolver, las tarjetas de WhatsApp leían el sobre -> 0.
    return desenvolverSobre<WhatsAppCampaignStatsDTO>(response.data);
  },

  // Obtener campañas por estado
  getByStatus: async (
    status: CampaignStatus
  ): Promise<WhatsAppCampaignDTO[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/by-status/${status}`
    );
    return response.data;
  },

  // Obtener campañas por tipo
  getByType: async (type: string): Promise<WhatsAppCampaignDTO[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/by-type/${type}`
    );
    return response.data;
  },

  // Obtener campañas programadas
  getScheduled: async (): Promise<WhatsAppCampaignDTO[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/scheduled`
    );
    return response.data;
  },

  // Obtener campañas en ejecución
  getRunning: async (): Promise<WhatsAppCampaignDTO[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/running`
    );
    return response.data;
  },

  // Actualizar estado de campaña
  updateStatus: async (
    id: string,
    status: CampaignStatus
  ): Promise<WhatsAppCampaignDTO> => {
    const response = await privateApi.patch(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/${id}/status`,
      { status }
    );
    return response.data;
  },

  // Enviar campaña inmediatamente
  send: async (id: string): Promise<void> => {
    await privateApi.post(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/${id}/send`
    );
  },

  // Programar campaña
  schedule: async (
    id: string,
    scheduledAt: Date
  ): Promise<WhatsAppCampaignDTO> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/${id}/schedule`,
      {
        scheduledAt: scheduledAt.toISOString(),
      }
    );
    return response.data;
  },

  // Webhook para recibir eventos de WhatsApp
  webhook: async (webhookData: WhatsAppWebhookEvent): Promise<void> => {
    await publicApi.post(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/webhook`,
      webhookData
    );
  },

  // Vista previa de mensaje
  preview: async (
    id: string,
    recipientPhone?: string
  ): Promise<{ messagePreview: string }> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/${id}/preview`,
      {
        params: { recipientPhone },
      }
    );
    return response.data;
  },

  // Obtener destinatarios de campaña
  getRecipients: async (id: string): Promise<CampaignRecipient[]> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/${id}/recipients`
    );
    return response.data;
  },

  // Enviar mensaje de prueba
  sendTest: async (id: string, testPhones: string[]): Promise<void> => {
    await privateApi.post(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/${id}/test`,
      {
        testPhones,
      }
    );
  },

  // Validar números de WhatsApp
  validatePhones: async (
    phones: string[]
  ): Promise<{ valid: string[]; invalid: string[] }> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/whatsapp-campaigns/validate-phones`,
      {
        phones,
      }
    );
    return response.data;
  },
};

// === CUENTAS SOCIALES CONECTADAS (GH#1632) ===
// `GET /marketing/social/integrations` (social-oauth.controller.ts) ya existía y el
// dashboard no lo llamaba. Devuelve la cuenta sin tokens.
export interface SocialIntegrationSummary {
  id: string;
  provider: 'facebook' | 'instagram' | 'linkedin' | 'twitter' | 'tiktok';
  accountId?: string;
  accountName?: string;
  status: 'active' | 'expired' | 'revoked' | 'error';
  isExpired?: boolean;
  isOperational?: boolean;
}

export const socialIntegrationsApi = {
  list: async (): Promise<SocialIntegrationSummary[]> => {
    const response = await privateApi.get(`${MARKETING_BASE_URL}/social/integrations`);
    const data = response.data;
    return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  },
};

// === SOCIAL POSTS API ===

export const socialPostsApi = {
  create: async (data: SocialPostCreatePayload): Promise<MarketingSocialPost> => {
    const response = await privateApi.post(SOCIAL_POSTS_BASE_URL, data);
    return response.data;
  },

  update: async (
    id: string,
    data: SocialPostUpdatePayload
  ): Promise<MarketingSocialPost> => {
    const response = await privateApi.patch(`${SOCIAL_POSTS_BASE_URL}/${id}`, data);
    return response.data;
  },

  updateStatus: async (
    id: string,
    status: MarketingSocialPost['status']
  ): Promise<MarketingSocialPost> => {
    const response = await privateApi.patch(`${SOCIAL_POSTS_BASE_URL}/${id}/status`, {
      status,
    });
    return response.data;
  },

  getAll: async (filters?: SocialPostFilterRequest): Promise<SocialPostListResponse> => {
    const query = buildSocialPostParams(filters);
    const response = await privateApi.get(`${SOCIAL_POSTS_BASE_URL}${query}`);
    return response.data;
  },

  getById: async (id: string): Promise<MarketingSocialPost> => {
    const response = await privateApi.get(`${SOCIAL_POSTS_BASE_URL}/${id}`);
    return response.data;
  },

  duplicate: async (id: string): Promise<MarketingSocialPost> => {
    const response = await privateApi.post(`${SOCIAL_POSTS_BASE_URL}/${id}/duplicate`);
    return response.data;
  },

  getStats: async (): Promise<MarketingPublicationStats> => {
    const response = await privateApi.get(`${SOCIAL_POSTS_BASE_URL}/stats`);
    return desenvolverSobre<MarketingPublicationStats>(response.data);
  },

  getScheduler: async (limit = 20): Promise<MarketingSchedulerSlot[]> => {
    const response = await privateApi.get(
      `${SOCIAL_POSTS_BASE_URL}/scheduler${limit ? `?limit=${limit}` : ''}`
    );
    const slots = desenvolverSobre<unknown>(response.data);
    // `any` a propósito: el mapeo produce la forma del backend, como antes del fix.
    const lista: any = Array.isArray(slots) ? slots : [];
    return lista.map((slot: any) => ({
      id: slot.id,
      postId: slot.postId ?? slot.id,
      network: slot.network,
      status: slot.status,
      start: slot.start ? new Date(slot.start).toISOString() : '',
      end: slot.end ? new Date(slot.end).toISOString() : '',
    }));
  },
};

// === TEMPLATES API ===

export const templatesApi = {
  // Email Templates
  email: {
    getAll: async (): Promise<EmailTemplateDTO[]> => {
      const response = await privateApi.get(
        `${MARKETING_BASE_URL}/email-templates`
      );
      return response.data;
    },

    getById: async (id: string): Promise<EmailTemplateDTO> => {
      const response = await privateApi.get(
        `${MARKETING_BASE_URL}/email-templates/${id}`
      );
      return response.data;
    },

    create: async (
      data: Partial<EmailTemplateDTO>
    ): Promise<EmailTemplateDTO> => {
      const response = await privateApi.post(
        `${MARKETING_BASE_URL}/email-templates`,
        data
      );
      return response.data;
    },

    update: async (
      id: string,
      data: Partial<EmailTemplateDTO>
    ): Promise<EmailTemplateDTO> => {
      const response = await privateApi.patch(
        `${MARKETING_BASE_URL}/email-templates/${id}`,
        data
      );
      return response.data;
    },

    delete: async (id: string): Promise<void> => {
      await privateApi.delete(`${MARKETING_BASE_URL}/email-templates/${id}`);
    },

    duplicate: async (
      id: string,
      newName: string
    ): Promise<EmailTemplateDTO> => {
      const response = await privateApi.post(
        `${MARKETING_BASE_URL}/email-templates/${id}/duplicate`,
        {
          name: newName,
        }
      );
      return response.data;
    },
  },

  // WhatsApp Templates
  whatsapp: {
    getAll: async (): Promise<WhatsAppTemplateDTO[]> => {
      const response = await privateApi.get(
        `${MARKETING_BASE_URL}/whatsapp-templates`
      );
      return response.data;
    },

    getById: async (id: string): Promise<WhatsAppTemplateDTO> => {
      const response = await privateApi.get(
        `${MARKETING_BASE_URL}/whatsapp-templates/${id}`
      );
      return response.data;
    },

    create: async (
      data: Partial<WhatsAppTemplateDTO>
    ): Promise<WhatsAppTemplateDTO> => {
      const response = await privateApi.post(
        `${MARKETING_BASE_URL}/whatsapp-templates`,
        data
      );
      return response.data;
    },

    update: async (
      id: string,
      data: Partial<WhatsAppTemplateDTO>
    ): Promise<WhatsAppTemplateDTO> => {
      const response = await privateApi.patch(
        `${MARKETING_BASE_URL}/whatsapp-templates/${id}`,
        data
      );
      return response.data;
    },

    delete: async (id: string): Promise<void> => {
      await privateApi.delete(`${MARKETING_BASE_URL}/whatsapp-templates/${id}`);
    },

    // Enviar template para aprobación de WhatsApp
    submitForApproval: async (id: string): Promise<WhatsAppTemplateDTO> => {
      const response = await privateApi.post(
        `${MARKETING_BASE_URL}/whatsapp-templates/${id}/submit`
      );
      return response.data;
    },

    // Sincronizar templates aprobados desde WhatsApp Business API
    syncApproved: async (): Promise<{
      synced: number;
      templates: WhatsAppTemplateDTO[];
    }> => {
      const response = await privateApi.post(
        `${MARKETING_BASE_URL}/whatsapp-templates/sync`
      );
      return response.data;
    },
  },
};

// === AUDIENCE & SEGMENTATION API ===

export const audienceApi = {
  // Obtener audiencia total
  getAudienceSize: async (
    segmentationCriteria?: any
  ): Promise<{ total: number; breakdown: any }> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/audience/size`,
      { segmentationCriteria }
    );
    return response.data;
  },

  // Obtener lista de contactos para una segmentación
  getSegmentedContacts: async (
    segmentationCriteria: any,
    limit?: number
  ): Promise<any[]> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/audience/segment`,
      {
        segmentationCriteria,
        limit,
      }
    );
    return response.data;
  },

  // Validar criterios de segmentación
  validateSegmentation: async (
    criteria: any
  ): Promise<{ valid: boolean; errors?: string[] }> => {
    const response = await privateApi.post(
      `${MARKETING_BASE_URL}/audience/validate`,
      { criteria }
    );
    return response.data;
  },

  // Obtener opciones de segmentación disponibles
  getSegmentationOptions: async (): Promise<{
    leadStatuses: string[];
    leadSources: string[];
    propertyTypes: string[];
    locations: string[];
    agents: any[];
    tags: string[];
  }> => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/audience/segmentation-options`
    );
    return response.data;
  },
};

// === ANALYTICS & REPORTS API ===

export const marketingReportsApi = {
  // Reporte de rendimiento general
  getOverallPerformance: async (dateFrom?: Date, dateTo?: Date) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom.toISOString());
    if (dateTo) params.append('dateTo', dateTo.toISOString());

    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/reports/overall-performance?${params.toString()}`
    );
    return response.data;
  },

  // Comparación Email vs WhatsApp
  getChannelComparison: async (period: 'week' | 'month' | 'quarter') => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/reports/channel-comparison?period=${period}`
    );
    return response.data;
  },

  // ROI de campañas
  getCampaignROI: async (campaignIds?: string[]) => {
    const params = new URLSearchParams();
    if (campaignIds?.length) {
      campaignIds.forEach(id => params.append('campaignIds', id));
    }

    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/reports/campaign-roi?${params.toString()}`
    );
    return response.data;
  },

  // Tendencias de engagement
  getEngagementTrends: async (
    period: 'day' | 'week' | 'month',
    last: number
  ) => {
    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/reports/engagement-trends?period=${period}&last=${last}`
    );
    return response.data;
  },

  // Reporte de conversión de leads generados por marketing
  getLeadConversion: async (
    campaignId?: string,
    dateFrom?: Date,
    dateTo?: Date
  ) => {
    const params = new URLSearchParams();
    if (campaignId) params.append('campaignId', campaignId);
    if (dateFrom) params.append('dateFrom', dateFrom.toISOString());
    if (dateTo) params.append('dateTo', dateTo.toISOString());

    const response = await privateApi.get(
      `${MARKETING_BASE_URL}/reports/lead-conversion?${params.toString()}`
    );
    return response.data;
  },
};

// === INTEGRATIONS API ===

export const marketingIntegrationsApi = {
  // Configuración de WhatsApp Business API
  whatsapp: {
    getConfig: async () => {
      const response = await privateApi.get(
        `${MARKETING_BASE_URL}/integrations/whatsapp/config`
      );
      return response.data;
    },

    updateConfig: async (config: any) => {
      const response = await privateApi.patch(
        `${MARKETING_BASE_URL}/integrations/whatsapp/config`,
        config
      );
      return response.data;
    },

    testConnection: async () => {
      const response = await privateApi.post(
        `${MARKETING_BASE_URL}/integrations/whatsapp/test`
      );
      return response.data;
    },
  },

  // Configuración de Email (SMTP/SendGrid/etc.)
  email: {
    getConfig: async () => {
      const response = await privateApi.get(
        `${MARKETING_BASE_URL}/integrations/email/config`
      );
      return response.data;
    },

    updateConfig: async (config: any) => {
      const response = await privateApi.patch(
        `${MARKETING_BASE_URL}/integrations/email/config`,
        config
      );
      return response.data;
    },

    testConnection: async (testEmail: string) => {
      const response = await privateApi.post(
        `${MARKETING_BASE_URL}/integrations/email/test`,
        { testEmail }
      );
      return response.data;
    },
  },
};

// Exportar todas las APIs del Marketing
export const marketingApi = {
  emailCampaigns: emailCampaignsApi,
  whatsappCampaigns: whatsappCampaignsApi,
  socialPosts: socialPostsApi,
  templates: templatesApi,
  audience: audienceApi,
  reports: marketingReportsApi,
  integrations: marketingIntegrationsApi,
};

export default marketingApi;
